import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setBooking(r.data.booking);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status) || attempts.current >= 8) {
          setState("error");
          return;
        }
      } catch {
        if (attempts.current >= 8) {
          setState("error");
          return;
        }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="noise-overlay" />
      <div className="relative max-w-lg w-full bg-white p-10 md:p-14 text-center">
        <img src="/logo-shamar.png" alt="Shamar" className="h-16 w-auto mx-auto mb-8" />
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-[#C9A03C] mx-auto animate-spin mb-6" />
            <h1 className="font-serif-lux text-2xl text-[#0A192F] mb-3">{t.payment.checking}</h1>
            <p className="text-sm text-[#0A192F]/60 font-light">{t.payment.checkingSub}</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 className="w-12 h-12 text-[#C9A03C] mx-auto mb-6" strokeWidth={1.5} />
            <p className="font-script text-[#C9A03C] text-3xl mb-3">{t.payment.thanks}</p>
            <h1 className="font-serif-lux text-2xl text-[#0A192F] mb-6">{t.payment.confirmed}</h1>
            {booking && (
              <div className="border border-[#C9A03C]/30 p-6 text-left text-sm text-[#0A192F]/80 font-light space-y-2 mb-8">
                <p><span className="text-[#C9A03C]">{t.payment.service}</span> {booking.service_label}</p>
                <p><span className="text-[#C9A03C]">{t.payment.date}</span> {booking.booking_date} — {booking.time_slot}</p>
                <p><span className="text-[#C9A03C]">{t.payment.amount}</span> {booking.amount.toFixed(0)} € {booking.payment_option === "acompte" ? t.payment.deposit : ""}</p>
              </div>
            )}
            <p className="text-sm text-[#0A192F]/60 font-light mb-8">{t.payment.emailNote}</p>
            <Link
              to="/"
              data-testid="payment-back-home"
              className="inline-block px-8 py-3.5 bg-[#0A192F] text-[#F0F0F0] text-[13px] tracking-[0.16em] uppercase hover:bg-[#C9A03C] hover:text-[#050B14] transition-colors duration-400"
            >
              {t.payment.back}
            </Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <AlertTriangle className="w-10 h-10 text-[#C9A03C] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif-lux text-2xl text-[#0A192F] mb-3">{t.payment.errTitle}</h1>
            <p className="text-sm text-[#0A192F]/60 font-light mb-8">{t.payment.errText}</p>
            <Link to="/" className="inline-block px-8 py-3.5 bg-[#0A192F] text-[#F0F0F0] text-[13px] tracking-[0.16em] uppercase hover:bg-[#C9A03C] hover:text-[#050B14] transition-colors duration-400">
              {t.payment.back}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
